import React from 'react';
const { useState, useEffect, useMemo } = React;
import { AirtableAPI, TABLE_IDS, CURRENT_USER } from '../../globals.js';
import QuoteList, { openQuoteFile } from './QuoteList.jsx';
import PayerPayments from './PayerPayments.jsx';
import { chainsOf } from '../../quoteChains.js';
import { quoteErrorMessage } from '../../quoteActions.js';
import { quotesOfContact, latestRequestOf, newContactQuoteFields, reusableDraftOf } from '../../personQuotes.js';

// The quotes of one contact, and a new one, from Contactos and from Personas (the same block). Each one opens
// its single file in «Cotizaciones y pagos» (the same from every access).
// By the contact's id only: never the quotes of other contacts of the same company. Who is quoted
// is chosen explicitly (the person or the company): a company linked to the contact does not mean
// contracting in its name. No form is needed; the answers of a request of that contact, if any,
// are only read for the internal analysis. Writes go through the commercial path (airtable-proxy)
// and publish-proposal, which check permissions and the quote rules (src/quoteRules.js).

const muted = { fontSize: 12, color: 'var(--globant-muted)' };

/**
 * contact: { id, name }; accounts: the contact's companies [{ id, name }];
 * intakes (optional): Diagnostic Intake records already loaded; otherwise they are read here.
 */
export default function ContactQuotes({ contact, accounts = [], intakes = null, api = null, tableId = '', onChanged = null }) {
  const a = api || new AirtableAPI();
  const table = tableId || TABLE_IDS.diagnosticIntake;
  const [rows, setRows] = useState(intakes);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null); // { recipient, linkAccount }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { if (intakes) setRows(intakes); }, [intakes]);
  useEffect(() => {
    if (intakes) return;
    a.fetchTable(table).then(setRows).catch(e => setLoadError(e.message || 'No se pudo leer'));
  }, [contact?.id]);

  const quotes = useMemo(() => quotesOfContact(rows || [], contact?.id), [rows, contact?.id]);
  const request = useMemo(() => latestRequestOf(rows || [], contact?.id), [rows, contact?.id]);
  const company = accounts.length === 1 ? accounts[0] : null;
  // One quote with its history (src/quoteChains.js), as in «Cotizaciones y pagos» and the company
  // Whole chains: every version of a quote that has a version of this contact (an earlier one may lack the contact)
  const chains = useMemo(() => chainsOf(rows || []).filter(c => c.versions.some(v => (v.record.fields?.['Stakeholder'] || []).includes(contact?.id))), [rows, contact?.id]);
  // Personal quotes are the administration's only (the server checks it: src/commercialScope.js › ADMIN_ONLY_TABLES)
  const canPersonal = CURRENT_USER?.role === 'admin';

  const create = async () => {
    setBusy(true); setMsg('');
    try {
      const fields = newContactQuoteFields({ stakeholderId: contact.id, contactName: contact.name, recipient: form.recipient, account: company, linkAccount: form.linkAccount });
      const existing = reusableDraftOf(quotes, { recipient: form.recipient, accountId: (fields['Account'] || [])[0] || '' });
      if (existing) { setForm(null); openQuoteFile(existing.id); return; }
      const rec = await a.createRecord(table, fields);
      setRows(prev => [...(prev || []), { ...rec, createdTime: rec.createdTime || new Date().toISOString(), fields: { ...fields, ...(rec.fields || {}) } }]);
      setForm(null);
      if (onChanged) onChanged();
      // The new quote opens in its file, the same from every access
      openQuoteFile(rec.id);
    } catch (e) { setMsg(quoteErrorMessage(e, 'No se pudo crear')); }
    finally { setBusy(false); }
  };

  if (!contact?.id) return null;
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 10, border: '1px solid var(--globant-border)' }} aria-label="Cotizaciones del contacto">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Cotizaciones y contrataciones</div>
        {!form && <button type="button" className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={() => { setForm({ recipient: '', linkAccount: false }); setMsg(''); }}>Nueva cotización</button>}
      </div>
      <div style={{ ...muted, marginTop: 2 }}>Solo las de este contacto. Las de otros contactos de su empresa no aparecen aquí ni en su espacio.{canPersonal ? ' Las personales las ven solo la administración y la persona, en su espacio.' : ''}</div>

      {form && (
        <div style={{ display: 'grid', gap: 6, marginTop: 8, padding: 8, borderRadius: 8, background: 'rgba(30,42,44,0.03)' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>¿A quién se cotiza?</div>
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', opacity: canPersonal ? 1 : 0.5 }}>
            <input type="radio" name={`rcp-${contact.id}`} disabled={!canPersonal} checked={form.recipient === 'person'} onChange={() => setForm({ ...form, recipient: 'person' })} />
            {canPersonal ? `A ${contact.name || 'la persona'}, por su cuenta${company ? ` (aunque trabaje en ${company.name})` : ''}.` : 'A la persona, por su cuenta: solo la administración prepara cotizaciones personales.'}
          </label>
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', opacity: company ? 1 : 0.5 }}>
            <input type="radio" name={`rcp-${contact.id}`} disabled={!company} checked={form.recipient === 'company'} onChange={() => setForm({ ...form, recipient: 'company', linkAccount: false })} />
            {company ? `A ${company.name}, con ${contact.name || 'este contacto'} como contacto.` : accounts.length > 1 ? 'A la empresa: el contacto tiene varias empresas; elegí una en su ficha primero.' : 'A la empresa: el contacto no tiene empresa vinculada.'}
          </label>
          {form.recipient === 'person' && company && (
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', marginLeft: 18 }}>
              <input type="checkbox" checked={form.linkAccount} onChange={e => setForm({ ...form, linkAccount: e.target.checked })} />
              Asociar también a {company.name} como referencia (no la paga ni la recibe).
            </label>
          )}
          <div style={muted}>Sin formulario previo. {request ? 'Se leen las respuestas de su última solicitud para el análisis interno; no se copian.' : 'No tiene una solicitud con respuestas: no hace falta.'}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="action-btn btn-primary" style={{ fontSize: 11 }} disabled={busy || !form.recipient} onClick={create}>{busy ? 'Creando…' : 'Crear borrador'}</button>
            <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} disabled={busy} onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {loadError && <div role="alert" style={{ ...muted, color: '#B4362C', marginTop: 6 }}>No se pudieron leer las cotizaciones ({loadError}).</div>}
      {!loadError && rows && !chains.length && !form && <div style={{ ...muted, marginTop: 6 }}>Sin cotizaciones para este contacto.</div>}
      {chains.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <QuoteList chains={chains} titleOf={(c) => (c.current || c.versions[c.versions.length - 1]).record.fields?.['Name'] || 'Cotización'}
            subtitleOf={(c) => (c.kind === 'personal' ? 'Para: la persona' : c.kind === 'company' ? 'Para: la empresa' : '')} />
        </div>
      )}
      {/* Her payments and balances: only her own charges, as payer (never her company's) */}
      <div style={{ marginTop: 10 }}><PayerPayments payer={{ type: 'person', id: contact.id }} name={contact.name} intakes={rows || []} /></div>
      {msg && <div role="status" style={{ ...muted, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
